/** @format */

import React, {useState, useRef, useEffect, createContext, useContext} from 'react';
import {ChevronDown, Check} from 'lucide-react';

const SelectContext = createContext();

export function Select({
    value,
    placeholder,
    onChange,
    children,
    className = '',
    direction = 'bottom', // top | bottom
    name,
    onChangeValue
}) {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const ref = useRef();

    // Go ahead and just send the value, not the full event
    onChange = onChangeValue ? ({target: {value}}) => onChangeValue(value) : onChange;

    const handleSelect = (val, label) => {
        setOpen(false);
        setSelected(label);
        onChange?.({target: {value: val, name}});
    };

    useEffect(() => {
        const handleClickOutside = e => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function findSelectedItem() {
        let selection = null;
        function iterate(children = []) {
            children.forEach(child => {
                if (!child.props && !Array.isArray(child)) return;
                if (Array.isArray(child)) iterate(child);
                else if (child.props.value === value) {
                    selection = child;
                } else if (
                    Array.isArray(child.props.children) &&
                    child.props.children?.length > 0
                ) {
                    iterate(child.props.children);
                }
            });
        }

        iterate(children);

        return selection;
    }

    useEffect(() => {
        const selection = findSelectedItem(children);
        if (selection) setSelected(selection.props.children);
    }, []);

    const selectionOrPlaceHolder = selected || placeholder || 'Select an option';

    const heightClass = className.includes('tw-h-') ? '' : 'tw-h-10';

    const positionClass = direction === 'bottom' ? '' : 'tw-bottom-[50px] ';

    return (
        <SelectContext.Provider value={{value, onSelect: handleSelect}}>
            <div
                ref={ref}
                className={`tw-relative tw-w-full ${className}`}
                data-component-id="ui-select">
                <div
                    onClick={() => setOpen(o => !o)}
                    className={`
            tw-flex ${heightClass} tw-w-full tw-items-center tw-justify-between
            tw-rounded-md tw-border tw-border-indigo-400 tw-bg-white tw-px-3 tw-text-base
            tw-placeholder:text-gray-500 focus:tw-outline-none focus:tw-border-primary
            tw-transition tw-shadow-sm hover:tw-border-primary
          `}>
                    <span className="tw-line-clamp-1 tw-text-gray-900">
                        {selectionOrPlaceHolder}
                    </span>
                    <ChevronDown className="tw-h-4 tw-w-4 tw-opacity-50" />
                </div>

                {open && (
                    <div
                        className={`${positionClass}
              tw-absolute tw-z-50 tw-mt-1 tw-max-h-96 tw-min-w-full
              tw-overflow-auto tw-rounded-md tw-border tw-bg-white
              tw-shadow-md tw-animate-in tw-fade-in-0
            `}>
                        {children}
                    </div>
                )}
            </div>
        </SelectContext.Provider>
    );
}

export function SelectGroup({children, className = ''}) {
    return <div className={` tw-p-1 ${className}`}>{children}</div>;
}

export function SelectLabel({children, className = ''}) {
    return (
        <div
            className={`
      tw-py-1.5 tw-pl-2 tw-text-primary tw-text-sm tw-font-semibold ${className}
    `}>
            {children}
        </div>
    );
}

export function SelectItem({value, children, className = ''}) {
    const {value: selectedValue, onSelect} = useContext(SelectContext);
    const isSelected = selectedValue === value;

    return (
        <div
            onClick={() => onSelect(value, children)}
            className={`
        tw-relative tw-flex tw-w-full tw-cursor-default tw-select-none
        tw-items-center tw-rounded-sm tw-py-1.5 tw-pl-8  tw-pr-2 tw-text-sm
        tw-outline-none hover:tw-bg-indigo-50
        ${isSelected ? 'tw-bg-indigo-100 tw-font-semibold tw-text-primary' : ''}
        ${className}
      `}>
            <span className="tw-absolute tw-left-2 tw-flex tw-h-3.5 tw-w-3.5 tw-items-center tw-justify-center">
                {isSelected && <Check className="tw-h-4 tw-w-4 tw-text-primary" />}
            </span>
            {children}
        </div>
    );
}
